import React, { useEffect, useMemo, useRef, useState } from "react";
import ViewWorkOrder, { ViewWorkOrderHandle } from "../../CustomerTransaction/Components/ViewWorkOrder";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useGetAllInvoiceQuery } from "../../../Api/attoDeskApi";
import { useNavigate } from "react-router";
import {
  ICompanyDetails,
  ICustomer,
  ICustomerProject,
} from "../../../Api/Interface/api.interface";
import dayjs, { Dayjs } from "dayjs";
import { formatDateTime, toAppTz } from "../../../Core/utils/dateTime";
import { TransferType, WorkOrderStatus } from "../../../Core/Enum/enum";
import { useParams, Link } from "react-router-dom";
import ReactToPrint from "react-to-print";
import LocalPrintshopSharpIcon from "@mui/icons-material/LocalPrintshopSharp";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import moment from "moment";
import axios from "axios";
import placeholderImage from "../../../Images/fav.png";

// ─── Status helpers ───────────────────────────────────────────────────────────
const STATUS_CFG: Record<number, { label: string; color: string; bg: string }> = {
  [WorkOrderStatus.Paid]:      { label: "Paid",      color: "#16a34a", bg: "#dcfce7" },
  [WorkOrderStatus.Cancel]:    { label: "Cancelled", color: "#dc2626", bg: "#fee2e2" },
  [WorkOrderStatus.Pending]:   { label: "Pending",   color: "#d97706", bg: "#fef3c7" },
  [WorkOrderStatus.Initiated]: { label: "Initiated", color: "#2563eb", bg: "#dbeafe" },
};
const getStatus = (code: number) =>
  STATUS_CFG[code] ?? { label: "Unknown", color: "#64748b", bg: "#f1f5f9" };

const cap = (s?: string | null) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

// ─── Interfaces ───────────────────────────────────────────────────────────────
interface IProduct {
  productName: string;
  productDescription: string;
  productImg: string | null;
  numberOfPassengers: number | null;
  busCount?: number | null;
  transferType: string;
  transferTypeFrom: string;
  transferTypeTo: string;
  departureDateTime: Dayjs | null;
  returnDateTime: Dayjs | null;
  departureAddress: string;
  destinationAddress: string;
  quantity: number | null;
  price: number | null;
  taxAmount: number | null;
  unitTotalWithDiscount: number;
  pickups?: any[];
  dropoffs?: any[];
  returnPickups?: any[];
  returnDropoffs?: any[];
}

interface IDiscount { value: number | null; description: string; }

interface IQuoteWorkOrder {
  id: number;
  quoteWorkOrderCompanyId: number;
  quoteWorkOrderCustomerId: string;
  quoteWorkOrderCustomerProjectId: string;
  quoteWorkOrderNo: string;
  quoteWorkOrderIssueDate: Dayjs;
  quoteWorkOrderDueDate: Dayjs;
  quoteTripId: string;
  quoteTripName: string;
  quoteWorkOrderAmount: number | null;
  quoteWorkOrderStatus: number;
  quoteWorkOrderSale: IProduct[];
  quoteWorkOrderDiscount: string;
  quoteWorkOrderSubTotal: string;
  quoteWorkOrderSaleGrandTotal: string;
  quoteWorkOrderSaleDiscountDescription: IDiscount[];
  customer: ICustomer;
  company: ICompanyDetails;
  project: ICustomerProject;
  quoteWorkOrderCustomerRemark: string;
  quoteWorkOrderCustomerMemo: string;
  quoteWorkOrderSaleDiscount: string;
  quoteWorkOrderSaleSubTotal: string;
  created_by: number;
  created_at: string;
  mainStatus: number;
  invoiceId: number;
  InvoiceNo: string;
  amount_paid: number;
  po_number: string;
  work_order_id: string;
}

interface Payment {
  id: number;
  transaction_id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  payment_mode: string;
  description: string;
  file_path?: string;
  created_at: string;
  updated_at: string;
  date: string;
  is_return: number;
  is_deleted: number;
}

// ─── InfoRow helper ───────────────────────────────────────────────────────────
const InfoRow = ({ label, value }: { label: string; value?: string | null }) =>
  value ? (
    <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", minWidth: 52, letterSpacing: 0.4, textTransform: "uppercase", pt: "1px" }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, color: "#475569" }}>{value}</Typography>
    </Box>
  ) : null;

// ─── Component ────────────────────────────────────────────────────────────────
const Index = () => {
  const navigate = useNavigate();
  const { id: InvoiceId } = useParams<{ id: string }>();
  const viewWorkOrderRef = useRef<ViewWorkOrderHandle>(null);
  const API_URL = process.env.REACT_APP_API_URL;
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;

  const [payments, setPayments] = useState<Payment[]>([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [openPayDialog, setOpenPayDialog] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [payData, setPayData] = useState({ date: new Date().toLocaleDateString("en-CA"), amount: "", method: "", description: "" });

  const { data: workOrderData, isLoading, isError } = useGetAllInvoiceQuery({
    searchText: "", id: String(InvoiceId),
    quoteWorkOrderCustomerId: "", quoteWorkOrderCustomerProjectId: "",
  });

  const WorkOrderDataList = useMemo(() => {
    if (workOrderData && Array.isArray(workOrderData.data) && workOrderData.data.length > 0)
      return workOrderData.data[0] as IQuoteWorkOrder;
    return null;
  }, [workOrderData]);

  const coursings = useMemo(() =>
    Array.isArray(WorkOrderDataList?.quoteWorkOrderSale) ? WorkOrderDataList!.quoteWorkOrderSale : [],
    [WorkOrderDataList]
  );
  const discountList = useMemo(() =>
    Array.isArray(WorkOrderDataList?.quoteWorkOrderSaleDiscountDescription)
      ? WorkOrderDataList!.quoteWorkOrderSaleDiscountDescription : [],
    [WorkOrderDataList]
  );

  useEffect(() => {
    let price = 0, tax = 0;
    coursings.forEach((p) => { price += p.price ?? 0; tax += p.taxAmount ?? 0; });
    setTotalPrice(price); setTotalTax(tax);
  }, [coursings]);

  useEffect(() => { fetchPayments(); }, [WorkOrderDataList?.work_order_id]);

  const fetchPayments = () => {
    if (!WorkOrderDataList?.work_order_id) return;
    axios.get(`${API_URL}/payments-by-workorder?work_order_id=${WorkOrderDataList.work_order_id}`)
      .then((res) => {
        const sorted = res.data.sort((a: Payment, b: Payment) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setPayments(sorted.filter((p: Payment) => p.is_deleted === 0 && p.is_return === 0));
      })
      .catch(console.error);
  };

  const handleSavePayment = async () => {
    if (!payData.amount || !payData.method) return;
    setPayLoading(true);
    try {
      const formData = new FormData();
      formData.append("work_order_id", String(WorkOrderDataList?.work_order_id || ""));
      formData.append("invoice_id", String(InvoiceId));
      formData.append("customer_id", String(WorkOrderDataList?.quoteWorkOrderCustomerId || ""));
      formData.append("amount", payData.amount);
      formData.append("paymentDate", payData.date);
      formData.append("paymentMethod", payData.method);
      formData.append("description", payData.description);
      const headers = { Authorization: `Bearer ${localStorage.getItem("token") || ""}` };
      await axios.post(`${API_URL}/save-payment`, formData, { headers });
      setOpenPayDialog(false);
      setPayData({ date: new Date().toLocaleDateString("en-CA"), amount: "", method: "", description: "" });
      fetchPayments();
    } catch {
      // silent
    } finally {
      setPayLoading(false);
    }
  };

  // Computed totals
  const grandTotal = parseFloat(WorkOrderDataList?.quoteWorkOrderSaleGrandTotal || "0");
  const totalPaid = payments.reduce((s, p) => s + Number(p.amount || 0), 0) || Number(WorkOrderDataList?.amount_paid || 0);
  const balance = Math.max(0, grandTotal - totalPaid);
  const status = getStatus(WorkOrderDataList?.quoteWorkOrderStatus ?? 0);

  // Address formatters
  const fmtAddr = (...parts: (string | undefined)[]) =>
    parts.filter(Boolean).join(", ");
  const companyAddr = fmtAddr(
    WorkOrderDataList?.company?.companyStreetNumber,
    WorkOrderDataList?.company?.companyStreetName,
    WorkOrderDataList?.company?.companyCity,
    WorkOrderDataList?.company?.companyProvince,
    WorkOrderDataList?.company?.companyPostalCode,
  );

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "40vh" }}>
        <CircularProgress sx={{ color: "#2f3891" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1200, mx: "auto" }}>

      {/* ── Header Card ─────────────────────────────────────────────────────── */}
      <Card sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.08)", border: "1px solid #e2e8f0", mb: 3 }}>
        <Box sx={{ height: 4, background: "linear-gradient(90deg,#2563eb,#2f3891,#0ea5e9)" }} />
        <Box sx={{ px: 3, py: 2.5, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>

          {/* Left: invoice identity */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, flexWrap: "wrap" }}>
              <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ReceiptLongOutlinedIcon sx={{ fontSize: 18, color: "#2f3891" }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>Invoice</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 20, color: "#0f172a", lineHeight: 1 }}>
                  {WorkOrderDataList?.InvoiceNo || "—"}
                </Typography>
              </Box>
              <Chip label={status.label} size="small"
                sx={{ height: 22, fontSize: 11, fontWeight: 700, color: status.color, bgcolor: status.bg, border: `1px solid ${status.color}33` }} />
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {WorkOrderDataList?.quoteWorkOrderNo && (
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>Quote: <strong>{WorkOrderDataList.quoteWorkOrderNo}</strong></Typography>
              )}
              {WorkOrderDataList?.po_number && (
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>PO: <strong>{WorkOrderDataList.po_number}</strong></Typography>
              )}
              {WorkOrderDataList?.quoteWorkOrderIssueDate && (
                <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                  Date: <strong>{formatDateTime(WorkOrderDataList.quoteWorkOrderIssueDate, "MMM D, YYYY")}</strong>
                </Typography>
              )}
              {WorkOrderDataList?.created_at && (
                <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                  {formatDateTime(WorkOrderDataList.created_at, "MMM D, YYYY · h:mm A")}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Right: actions */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <Button variant="outlined" size="small" startIcon={<ArrowBackIosIcon sx={{ fontSize: 12 }} />}
              onClick={() => navigate(-1)}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: 12, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b", "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" } }}>
              Back
            </Button>
            {balance > 0 ? (
              <Button variant="contained" size="small" onClick={() => setOpenPayDialog(true)}
                sx={{ textTransform: "none", fontWeight: 700, fontSize: 12, borderRadius: 1.5, bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" }, boxShadow: "none" }}>
                Process Payment
              </Button>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 1.5, px: 1.5, py: 0.5 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#16a34a" }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#16a34a" }}>Paid</Typography>
              </Box>
            )}
            <Button variant="outlined" size="small" startIcon={<ArticleOutlinedIcon sx={{ fontSize: 13 }} />}
              component={Link} to={`/work-order/history/${WorkOrderDataList?.work_order_id}`}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: 12, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#2f3891", "&:hover": { borderColor: "#c4b5fd", bgcolor: "#faf5ff" } }}>
              View Quote
            </Button>
            <ReactToPrint
              trigger={() => (
                <Button variant="contained" size="small" startIcon={<LocalPrintshopSharpIcon sx={{ fontSize: 13 }} />}
                  sx={{ textTransform: "none", fontWeight: 700, fontSize: 12, borderRadius: 1.5, bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" }, boxShadow: "none" }}>
                  Print Invoice
                </Button>
              )}
              content={() => viewWorkOrderRef.current?.getContentElement() ?? null}
              documentTitle={WorkOrderDataList?.InvoiceNo || "Invoice"}
            />
          </Box>
        </Box>
      </Card>

      <Grid container spacing={3}>

        {/* ── LEFT COLUMN ─────────────────────────────────────────────────── */}
        <Grid item xs={12} lg={8}>
          <Grid container spacing={3}>

            {/* Client Info Card */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", border: "1px solid #f1f5f9" }}>
                <Box sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: "#2f3891" }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 12, color: "#1e293b", textTransform: "uppercase", letterSpacing: 0.8 }}>Client</Typography>
                </Box>
                <Box sx={{ p: 2.5, display: "flex", gap: 2, alignItems: "flex-start" }}>
                  <Avatar sx={{ width: 48, height: 48, bgcolor: "#ede9fe", color: "#2f3891", fontWeight: 700, fontSize: 17, flexShrink: 0 }}>
                    {(WorkOrderDataList?.customer?.customerFirstName?.[0] || "").toUpperCase()}
                    {(WorkOrderDataList?.customer?.customerLastName?.[0] || "").toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    {WorkOrderDataList?.quoteWorkOrderCustomerProjectId ? (
                      <>
                        <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#1e293b", mb: 0.5 }}>
                          {WorkOrderDataList.project?.projectName}
                        </Typography>
                        <InfoRow label="Phone" value={WorkOrderDataList.project?.projectPhoneNumber} />
                        <InfoRow label="Email" value={WorkOrderDataList.project?.projectEmail} />
                      </>
                    ) : (
                      <>
                        <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#1e293b", mb: 0.25 }}>
                          {`${cap(WorkOrderDataList?.customer?.customerFirstName)} ${cap(WorkOrderDataList?.customer?.customerLastName)}`.trim()}
                        </Typography>
                        {WorkOrderDataList?.customer?.customerCompanyName && (
                          <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.75 }}>{WorkOrderDataList.customer.customerCompanyName}</Typography>
                        )}
                        <InfoRow label="Mob" value={WorkOrderDataList?.customer?.customerMobileNum} />
                        <InfoRow label="Email" value={WorkOrderDataList?.customer?.customerEmail} />
                        <InfoRow label="Tel" value={WorkOrderDataList?.customer?.customerCompanyPhoneNumebr} />
                      </>
                    )}
                  </Box>
                  {/* Issued By */}
                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>Issued By</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{WorkOrderDataList?.company?.companyName}</Typography>
                    {companyAddr && <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.25 }}>{companyAddr}</Typography>}
                    {WorkOrderDataList?.company?.companyMobileNumber && (
                      <Typography sx={{ fontSize: 11, color: "#64748b" }}>{WorkOrderDataList.company.companyMobileNumber}</Typography>
                    )}
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Services Card */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", border: "1px solid #f1f5f9" }}>
                <Box sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: "#2563eb" }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 12, color: "#1e293b", textTransform: "uppercase", letterSpacing: 0.8 }}>Services</Typography>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead sx={{ bgcolor: "#f8fafc" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, py: 1.5, px: 2.5 }}>Trip Information</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, py: 1.5, px: 2, textAlign: "right" }}>Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {coursings.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} align="center" sx={{ py: 4, color: "#94a3b8", fontSize: 13 }}>No services</TableCell>
                        </TableRow>
                      ) : coursings.map((product, index) => (
                        <TableRow key={index} sx={{ "&:hover": { bgcolor: "#fafcff" }, "&:last-child td": { borderBottom: 0 } }}>
                          <TableCell sx={{ py: 1.5, px: 2.5 }}>
                            {product.productName && (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                                <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>{product.productName}</Typography>
                                {product.numberOfPassengers && (
                                  <Chip label={`${product.numberOfPassengers} pax`} size="small" sx={{ height: 18, fontSize: 10, bgcolor: "#ede9fe", color: "#2f3891", fontWeight: 600 }} />
                                )}
                                {(product as any).busCount && (
                                  <Chip label={`Count: ${(product as any).busCount}`} size="small" sx={{ height: 18, fontSize: 10, bgcolor: "#dbeafe", color: "#2563eb", fontWeight: 600 }} />
                                )}
                                {product.transferType && (
                                  <Chip label={(Object.keys(TransferType) as (keyof typeof TransferType)[]).find((k) => TransferType[k] === String(product.transferType)) || product.transferType}
                                    size="small" sx={{ height: 18, fontSize: 10, bgcolor: "#f0fdf4", color: "#16a34a", fontWeight: 600 }} />
                                )}
                              </Box>
                            )}
                            {product.productDescription && (
                              <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.5, whiteSpace: "pre-wrap" }}>
                                {product.productDescription}
                              </Typography>
                            )}
                            {/* New format: pickups/dropoffs arrays */}
                            {(product.pickups || []).map((pu: any, pi: number) =>
                              (pu.address || pu.dateTime) ? (
                                <Typography key={pi} sx={{ fontSize: 12, color: "#475569", mb: 0.25 }}>
                                  <Box component="span" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                    {(product.pickups?.length || 0) > 1 ? `Pickup ${pi + 1}` : "Pickup"}:
                                  </Box>{" "}
                                  {pu.address}{pu.city ? `, ${pu.city}` : ""}
                                  {pu.dateTime && toAppTz(pu.dateTime)?.isValid() && <> · <strong>{formatDateTime(pu.dateTime, "DD-MMM-YY h:mm A")}</strong></>}
                                </Typography>
                              ) : null
                            )}
                            {(product.dropoffs || []).map((dr: any, di: number) =>
                              (dr.address || dr.dateTime) ? (
                                <Typography key={di} sx={{ fontSize: 12, color: "#475569", mb: 0.25 }}>
                                  <Box component="span" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                    {(product.dropoffs?.length || 0) > 1 ? `Dropoff ${di + 1}` : "Dropoff"}:
                                  </Box>{" "}
                                  {dr.address}{dr.city ? `, ${dr.city}` : ""}
                                  {dr.dateTime && toAppTz(dr.dateTime)?.isValid() && <> · <strong>{formatDateTime(dr.dateTime, "DD-MMM-YY h:mm A")}</strong></>}
                                </Typography>
                              ) : null
                            )}
                            {(product.returnPickups || []).map((pu: any, pi: number) =>
                              (pu.address || pu.dateTime) ? (
                                <Typography key={pi} sx={{ fontSize: 12, color: "#475569", mb: 0.25 }}>
                                  <Box component="span" sx={{ fontWeight: 700, color: "#1e293b" }}>Return Pickup:</Box>{" "}
                                  {pu.address}{pu.city ? `, ${pu.city}` : ""}
                                  {pu.dateTime && toAppTz(pu.dateTime)?.isValid() && <> · <strong>{formatDateTime(pu.dateTime, "DD-MMM-YY h:mm A")}</strong></>}
                                </Typography>
                              ) : null
                            )}
                            {(product.returnDropoffs || []).map((dr: any, di: number) =>
                              (dr.address || dr.dateTime) ? (
                                <Typography key={di} sx={{ fontSize: 12, color: "#475569", mb: 0.25 }}>
                                  <Box component="span" sx={{ fontWeight: 700, color: "#1e293b" }}>Return Dropoff:</Box>{" "}
                                  {dr.address}{dr.city ? `, ${dr.city}` : ""}
                                  {dr.dateTime && toAppTz(dr.dateTime)?.isValid() && <> · <strong>{formatDateTime(dr.dateTime, "DD-MMM-YY h:mm A")}</strong></>}
                                </Typography>
                              ) : null
                            )}
                            {/* Legacy flat fields */}
                            {!(product.pickups?.length) && product.departureAddress && (
                              <Typography sx={{ fontSize: 12, color: "#475569", mb: 0.25 }}>
                                <Box component="span" sx={{ fontWeight: 700, color: "#1e293b" }}>From:</Box>{" "}
                                {product.departureAddress}
                                {product.departureDateTime && toAppTz(product.departureDateTime)?.isValid() && <> · <strong>{formatDateTime(product.departureDateTime, "DD-MMM-YY h:mm A")}</strong></>}
                              </Typography>
                            )}
                            {!(product.dropoffs?.length) && product.destinationAddress && (
                              <Typography sx={{ fontSize: 12, color: "#475569" }}>
                                <Box component="span" sx={{ fontWeight: 700, color: "#1e293b" }}>To:</Box>{" "}
                                {product.destinationAddress}
                                {product.returnDateTime && toAppTz(product.returnDateTime)?.isValid() && <> · <strong>{formatDateTime(product.returnDateTime, "DD-MMM-YY h:mm A")}</strong></>}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 1.5, px: 2, textAlign: "right", fontWeight: 600, fontSize: 13, color: "#1e293b", verticalAlign: "top" }}>
                            ${(product.price ?? 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Totals */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2.5, pb: 2.5, mt: 1 }}>
                  <Box sx={{ minWidth: 260, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0", p: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                      <Typography sx={{ fontSize: 13, color: "#64748b" }}>Sub Total</Typography>
                      <Typography sx={{ fontSize: 13, color: "#1e293b" }}>${totalPrice.toFixed(2)}</Typography>
                    </Box>
                    {discountList.map((d, i) => (
                      <Box key={i} sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                        <Typography sx={{ fontSize: 13, color: "#64748b" }}>{d.description || "Discount"}</Typography>
                        <Typography sx={{ fontSize: 13, color: "#dc2626" }}>-${(d.value ?? 0).toFixed(2)}</Typography>
                      </Box>
                    ))}
                    {WorkOrderDataList?.quoteWorkOrderDiscount && parseFloat(WorkOrderDataList.quoteWorkOrderDiscount) !== 0 && (
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                        <Typography sx={{ fontSize: 13, color: "#64748b" }}>Total Discount</Typography>
                        <Typography sx={{ fontSize: 13, color: "#dc2626" }}>-${parseFloat(WorkOrderDataList.quoteWorkOrderDiscount).toFixed(2)}</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                      <Typography sx={{ fontSize: 13, color: "#64748b" }}>HST</Typography>
                      <Typography sx={{ fontSize: 13, color: "#1e293b" }}>${totalTax.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ borderTop: "1px solid #e2e8f0", my: 1 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>Grand Total</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>${grandTotal.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography sx={{ fontSize: 13, color: "#64748b" }}>Settled Amount</Typography>
                      <Typography sx={{ fontSize: 13, color: totalPaid > 0 ? "#16a34a" : "#64748b", fontWeight: totalPaid > 0 ? 600 : 400 }}>
                        ${totalPaid.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", bgcolor: balance === 0 ? "#f0fdf4" : "#fef2f2", borderRadius: 1.5, px: 1.5, py: 0.75 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: balance === 0 ? "#16a34a" : "#dc2626" }}>Balance</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: balance === 0 ? "#16a34a" : "#dc2626" }}>${balance.toFixed(2)}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>

          </Grid>
        </Grid>

        {/* ── RIGHT COLUMN ────────────────────────────────────────────────── */}
        <Grid item xs={12} lg={4}>
          <Grid container spacing={3}>

            {/* Payment History */}
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", border: "1px solid #f1f5f9" }}>
                <Box sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: "#10b981" }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 12, color: "#1e293b", textTransform: "uppercase", letterSpacing: 0.8 }}>Payment History</Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  {payments.length === 0 ? (
                    <Typography sx={{ fontSize: 12, color: "#94a3b8", textAlign: "center", py: 2 }}>No payments recorded</Typography>
                  ) : (
                    payments.map((payment) => (
                      <Box key={payment.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5, pb: 1.5, borderBottom: "1px solid #f1f5f9", "&:last-child": { mb: 0, pb: 0, borderBottom: 0 } }}>
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>${Number(payment.amount).toFixed(2)}</Typography>
                          <Typography sx={{ fontSize: 11, color: "#64748b" }}>{payment.payment_method}</Typography>
                          {payment.description && <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{payment.description}</Typography>}
                        </Box>
                        <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>
                          {formatDateTime(payment.payment_date, "DD MMM YYYY")}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>
              </Card>
            </Grid>

            {/* Notes & Invoice Footer */}
            {(WorkOrderDataList?.quoteWorkOrderCustomerRemark || WorkOrderDataList?.quoteWorkOrderCustomerMemo || WorkOrderDataList?.company?.companyFooterDetails) && (
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", border: "1px solid #f1f5f9" }}>
                  <Box sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: "#f59e0b" }} />
                    <Typography sx={{ fontWeight: 700, fontSize: 12, color: "#1e293b", textTransform: "uppercase", letterSpacing: 0.8 }}>Notes</Typography>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    {WorkOrderDataList?.quoteWorkOrderCustomerRemark && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>Special Notification</Typography>
                        <Typography sx={{ fontSize: 12, color: "#475569" }}>{WorkOrderDataList.quoteWorkOrderCustomerRemark}</Typography>
                      </Box>
                    )}
                    {WorkOrderDataList?.quoteWorkOrderCustomerMemo && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>Internal / Office Notes</Typography>
                        <Typography sx={{ fontSize: 12, color: "#475569" }}>{WorkOrderDataList.quoteWorkOrderCustomerMemo}</Typography>
                      </Box>
                    )}
                    {WorkOrderDataList?.company?.companyFooterDetails && (
                      <Box>
                        <Typography sx={{ fontSize: 12, color: "#475569" }}>{WorkOrderDataList.company.companyFooterDetails}</Typography>
                      </Box>
                    )}
                  </Box>
                </Card>
              </Grid>
            )}

          </Grid>
        </Grid>
      </Grid>

      {/* ── Hidden document for print/PDF ───────────────────────────────── */}
      <div style={{ position: "fixed", left: "-9999px", top: 0, width: "900px", pointerEvents: "none", zIndex: -1 }}>
        <ViewWorkOrder ref={viewWorkOrderRef} quoteId={String(WorkOrderDataList?.work_order_id || "")} type="email" />
      </div>

      {/* ── Payment Dialog ──────────────────────────────────────────────── */}
      <Dialog open={openPayDialog} onClose={() => setOpenPayDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
        <Box sx={{ height: 4, bgcolor: "#10b981" }} />
        <DialogTitle sx={{ pb: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Make a Payment</Typography>
          <IconButton size="small" onClick={() => setOpenPayDialog(false)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </DialogTitle>
        <Box sx={{ px: 3, pb: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>Date</Typography>
              <TextField size="small" type="date" fullWidth value={payData.date}
                onChange={(e) => setPayData({ ...payData, date: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>Amount</Typography>
              <TextField size="small" type="text" inputMode="decimal" fullWidth placeholder="0.00"
                value={payData.amount}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) setPayData({ ...payData, amount: v });
                }} />
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>Payment Method</Typography>
            <Select size="small" fullWidth displayEmpty value={payData.method}
              onChange={(e) => setPayData({ ...payData, method: e.target.value })}>
              <MenuItem value="" disabled><em>Select method</em></MenuItem>
              <MenuItem value="Cash">Cash</MenuItem>
              <MenuItem value="Credit Card">Credit Card</MenuItem>
              <MenuItem value="Debit Card">Debit Card</MenuItem>
              <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
              <MenuItem value="Cheque">Cheque</MenuItem>
              <MenuItem value="Purchase Order (PO)">Purchase Order (PO)</MenuItem>
              <MenuItem value="E-Transfer">E-Transfer</MenuItem>
            </Select>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5 }}>Description</Typography>
            <TextField size="small" fullWidth multiline rows={2} placeholder="Optional note…"
              value={payData.description}
              onChange={(e) => setPayData({ ...payData, description: e.target.value })} />
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => setOpenPayDialog(false)} disabled={payLoading}
            sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSavePayment}
            disabled={payLoading || !payData.amount || !payData.method}
            sx={{ textTransform: "none", fontWeight: 700, fontSize: 13, borderRadius: 1.5, bgcolor: "#10b981", "&:hover": { bgcolor: "#059669" }, boxShadow: "none", minWidth: 80 }}>
            {payLoading ? "Saving…" : "Pay"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default Index;
